import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AuthContext from "../../context/authProvider";
import ToastContext from "../../context/toastProvider";
import Loading from "../atoms/loading";
import { parseBotOpsSection } from "./botOpsSections";
import BotOpsSectionNav from "./botOpsSectionNav";
import BotOpsOperarPanel from "./botOpsOperarPanel";
import BotOpsConfigPanel from "./botOpsConfigPanel";
import BotOpsChannelPanel from "./botOpsChannelPanel";
import BotOpsSandboxChat from "./botOpsSandboxChat";
import BotOpsPipelineTour from "./botOpsPipelineTour";
import BotOpsTestesPanel from "./botOpsTestesPanel";
import {
    getKill,
    setKill,
    getSandboxKill,
    setSandboxKill,
    getConfig,
    getConfigById,
    saveConfig,
    listChannels,
    getHealth,
    reapplyConfig,
} from "./functions";
import { serializeConfigSnapshot, isConfigDirty } from "./configTrust";
import { applyConfigPatch } from "./applyConfigPatch";

const BotOpsPage = () => {
    const { user } = useContext(AuthContext);
    const { goodToast, badToast } = useContext(ToastContext);

    const [searchParams, setSearchParams] = useSearchParams();
    const section = parseBotOpsSection(searchParams.get("section"));

    const setSection = (id) => {
        const next = new URLSearchParams(searchParams);
        if (id === "operar") next.delete("section");
        else next.set("section", id);
        setSearchParams(next, { replace: true });
    };

    const handleFocusTab = useCallback(
        (tab) => {
            if (!tab) return;
            setSection(tab === "channel" ? "canal" : "config");
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchParams]
    );

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [killLoading, setKillLoading] = useState(false);
    const [sandboxKillLoading, setSandboxKillLoading] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [sandboxEnabled, setSandboxEnabled] = useState(true);
    const [activeVersion, setActiveVersion] = useState(null);
    const [versionCreatedAt, setVersionCreatedAt] = useState(null);
    const [modelo, setModelo] = useState("");
    const [temperatura, setTemperatura] = useState("");
    const [systemPrompt, setSystemPrompt] = useState("");
    const [guardrails, setGuardrails] = useState("");
    const [knowledge, setKnowledge] = useState([]);
    const [hasClosedChannels, setHasClosedChannels] = useState(false);
    const [configSnapshot, setConfigSnapshot] = useState(null);
    const [configHealth, setConfigHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [reapplyLoading, setReapplyLoading] = useState(false);
    const [acceptedPatchIds, setAcceptedPatchIds] = useState([]);

    const currentConfigFields = {
        systemPrompt,
        guardrails,
        knowledge,
        modelo,
        temperatura,
    };
    const dirty = isConfigDirty(configSnapshot, currentConfigFields);

    const captureSnapshot = (fields) => {
        setConfigSnapshot(serializeConfigSnapshot(fields));
        setAcceptedPatchIds([]);
    };

    const refreshHealth = async () => {
        if (!user?.token) return;
        setHealthLoading(true);
        const data = await getHealth(user.token);
        setHealthLoading(false);
        if (data?.type === "error") {
            setConfigHealth(null);
            return;
        }
        setConfigHealth(data);
    };

    const loadData = async () => {
        setLoading(true);
        const token = user?.token;

        try {
            const [killData, sandboxKillData, channelsData, configData] = await Promise.all([
                getKill(token),
                getSandboxKill(token),
                listChannels(token),
                getConfig(token),
            ]);

            if (killData?.type === "error") {
                badToast(killData.message || "Erro ao carregar kill switch");
            } else if (typeof killData?.enabled === "boolean") {
                setEnabled(killData.enabled);
            }

            if (sandboxKillData?.type === "error") {
                badToast(sandboxKillData.message || "Erro ao carregar kill do sandbox");
            } else if (typeof sandboxKillData?.enabled === "boolean") {
                setSandboxEnabled(sandboxKillData.enabled);
            }

            if (channelsData?.type !== "error" && Array.isArray(channelsData?.instances)) {
                setHasClosedChannels(channelsData.instances.some((i) => i.state === "close"));
            }

            if (!configData || configData?.type === "error") {
                badToast(configData?.message || "Erro ao carregar configuração");
                return;
            }

            setActiveVersion(configData.active_version ?? null);

            if (configData.active_version) {
                const versionData = await getConfigById(token, configData.active_version);
                if (versionData?.type === "error") {
                    badToast(versionData.message || "Erro ao carregar versão ativa");
                } else {
                    const nextPrompt = versionData.system_prompt ?? "";
                    const nextGuardrails = versionData.guardrails ?? "";
                    const nextModelo = versionData.modelo ?? "";
                    const nextTemperatura =
                        versionData.temperatura == null ? "" : String(versionData.temperatura);
                    const nextKnowledge = Array.isArray(versionData.knowledge)
                        ? versionData.knowledge.map((k) => ({
                              filename: k.filename ?? "",
                              content: k.content ?? "",
                          }))
                        : [];
                    setSystemPrompt(nextPrompt);
                    setGuardrails(nextGuardrails);
                    setModelo(nextModelo);
                    setTemperatura(nextTemperatura);
                    setKnowledge(nextKnowledge);
                    setVersionCreatedAt(versionData.created_at ?? null);
                    captureSnapshot({
                        systemPrompt: nextPrompt,
                        guardrails: nextGuardrails,
                        knowledge: nextKnowledge,
                        modelo: nextModelo,
                        temperatura: nextTemperatura,
                    });
                }
            } else {
                setSystemPrompt("");
                setGuardrails("");
                setModelo("");
                setTemperatura("");
                setKnowledge([]);
                setVersionCreatedAt(null);
                captureSnapshot({
                    systemPrompt: "",
                    guardrails: "",
                    knowledge: [],
                    modelo: "",
                    temperatura: "",
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (section === "config") {
            refreshHealth();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    const handleKillToggle = async () => {
        const nextEnabled = !enabled;

        if (!nextEnabled) {
            const confirmed = window.confirm("Desligar respostas automáticas?");
            if (!confirmed) return;
        }

        setKillLoading(true);
        const data = await setKill(user.token, nextEnabled);
        setKillLoading(false);

        if (data?.type === "error") {
            badToast(data.message || "Erro ao atualizar kill switch");
            return;
        }

        setEnabled(data.enabled ?? nextEnabled);
        goodToast(nextEnabled ? "Respostas automáticas ativadas" : "Respostas automáticas desligadas");
    };

    const handleSandboxKillToggle = async () => {
        const nextEnabled = !sandboxEnabled;

        if (!nextEnabled) {
            const confirmed = window.confirm("Desligar turno IA no sandbox?");
            if (!confirmed) return;
        }

        setSandboxKillLoading(true);
        const data = await setSandboxKill(user.token, nextEnabled);
        setSandboxKillLoading(false);

        if (data?.type === "error") {
            badToast(data.message || "Erro ao atualizar kill do sandbox");
            return;
        }

        setSandboxEnabled(data.enabled ?? nextEnabled);
        goodToast(
            nextEnabled ? "Sandbox ativado (turno IA ligado)" : "Sandbox desligado (sem turno IA)"
        );
    };

    const handleAddKnowledge = () => {
        setKnowledge((prev) => [...prev, { filename: "", content: "" }]);
    };

    const handleRemoveKnowledge = (index) => {
        setKnowledge((prev) => prev.filter((_, i) => i !== index));
    };

    const handleKnowledgeChange = (index, field, value) => {
        setKnowledge((prev) =>
            prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        );
    };

    const handleSave = async () => {
        const trimmedKnowledge = knowledge
            .map((k) => ({
                filename: (k.filename ?? "").trim(),
                content: k.content ?? "",
            }))
            .filter((k) => k.filename);

        setSaving(true);
        const data = await saveConfig(user.token, {
            system_prompt: systemPrompt,
            guardrails,
            knowledge: trimmedKnowledge,
            modelo,
            temperatura,
        });
        setSaving(false);

        if (data?.type === "error") {
            badToast(data.message || "Erro ao salvar configuração");
            return;
        }

        goodToast("Configuração salva!");
        setActiveVersion(data.active_version ?? null);
        if (data.version?.created_at) {
            setVersionCreatedAt(data.version.created_at);
        }
        captureSnapshot({
            systemPrompt,
            guardrails,
            knowledge: trimmedKnowledge,
            modelo,
            temperatura,
        });
        await refreshHealth();
    };

    const handleReapply = async () => {
        const confirmed = window.confirm(
            "Reaplicar a versão ativa do painel no bot (Redis), sem criar versão nova?"
        );
        if (!confirmed) return;
        setReapplyLoading(true);
        const data = await reapplyConfig(user.token);
        setReapplyLoading(false);
        if (data?.type === "error") {
            badToast(data.message || "Erro ao reaplicar");
            return;
        }
        goodToast("Configuração reaplicada no bot");
        await refreshHealth();
    };

    const handleApplyPatch = (patch) => {
        const result = applyConfigPatch(
            {
                systemPrompt,
                guardrails,
                knowledge,
                modelo,
                temperatura,
            },
            patch
        );
        if (!result.ok) {
            badToast(result.reason || "Não foi possível aplicar o patch");
            return;
        }
        setSystemPrompt(result.next.systemPrompt);
        setGuardrails(result.next.guardrails);
        setKnowledge(result.next.knowledge);
        setModelo(result.next.modelo);
        setTemperatura(result.next.temperatura);
        setAcceptedPatchIds((prev) =>
            prev.includes(patch.id) ? prev : [...prev, patch.id]
        );
        goodToast(
            result.alreadyApplied
                ? "Esse trecho já estava no editor — marcado como aceito"
                : "Patch aceito no editor — revise e Salve para publicar"
        );
    };

    if (loading) {
        return (
            <main className="p-4">
                <Loading classes="m-auto mt-8" />
            </main>
        );
    }

    return (
        <main className="p-4 max-w-5xl mx-auto">
            <header className="mb-6 text-center">
                <h1 className="text-2xl font-semibold text-[#d59106] tracking-tight">Bot Ops</h1>
                <p className="text-sm text-gray-400 mt-2 max-w-2xl mx-auto leading-relaxed">
                    Controle do bot omnichannel: kill switch, modelo, configuração do turno IA
                    nativo, canal WhatsApp, chat de teste, testes promptfoo e ajuda.
                </p>
            </header>

            {hasClosedChannels ? (
                <div
                    className="rounded-lg border border-red-500/50 bg-red-950/30 p-3 mb-4 text-sm text-red-200 text-center"
                    role="alert"
                >
                    Há linha(s) WhatsApp desconectada(s). Abra a seção Canal para ver o QR e
                    reconectar.
                </div>
            ) : null}

            <BotOpsSectionNav section={section} onSectionChange={setSection} />

            {section === "operar" && (
                <BotOpsOperarPanel
                    enabled={enabled}
                    sandboxEnabled={sandboxEnabled}
                    killLoading={killLoading}
                    sandboxKillLoading={sandboxKillLoading}
                    onKillToggle={handleKillToggle}
                    onSandboxKillToggle={handleSandboxKillToggle}
                    token={user.token}
                    onAfterMutation={loadData}
                    activeVersion={activeVersion}
                />
            )}

            {section === "config" && (
                <BotOpsConfigPanel
                    token={user.token}
                    activeVersion={activeVersion}
                    versionCreatedAt={versionCreatedAt}
                    modelo={modelo}
                    setModelo={setModelo}
                    temperatura={temperatura}
                    setTemperatura={setTemperatura}
                    systemPrompt={systemPrompt}
                    setSystemPrompt={setSystemPrompt}
                    guardrails={guardrails}
                    setGuardrails={setGuardrails}
                    knowledge={knowledge}
                    onAddKnowledge={handleAddKnowledge}
                    onRemoveKnowledge={handleRemoveKnowledge}
                    onKnowledgeChange={handleKnowledgeChange}
                    saving={saving}
                    onSave={handleSave}
                    dirty={dirty}
                    health={configHealth}
                    healthLoading={healthLoading}
                    onRefreshHealth={refreshHealth}
                    onReapply={handleReapply}
                    reapplyLoading={reapplyLoading}
                    onApplyPatch={handleApplyPatch}
                    acceptedPatchIds={acceptedPatchIds}
                />
            )}

            {section === "canal" && (
                <BotOpsChannelPanel
                    token={user.token}
                    onToastGood={goodToast}
                    onToastBad={badToast}
                    onChannelSummary={({ hasClosed }) => setHasClosedChannels(hasClosed)}
                    isActive
                />
            )}

            {section === "sandbox" && (
                <BotOpsSandboxChat
                    token={user.token}
                    sandboxEnabled={sandboxEnabled}
                    onToastGood={goodToast}
                    onToastBad={badToast}
                    isActive
                />
            )}

            {section === "ajuda" && (
                <BotOpsPipelineTour enabled={enabled} onFocusTab={handleFocusTab} />
            )}

            {section === "testes" && (
                <BotOpsTestesPanel
                    token={user?.token}
                    goodToast={goodToast}
                    badToast={badToast}
                />
            )}
        </main>
    );
};

export default BotOpsPage;
